import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import { VideoFactoryProject } from '@/types/resources';
import {
  VideoFactoryProjectStatus,
  VideoFactorySettingsType,
} from '@/utils/enums';
import { download } from '@/utils/helpers';

import AnimateImagesDrawer from './AnimateImagesDrawer';
import StatusBadge from './StatusBadge';

const Thumbnail = styled.img.attrs({ loading: 'lazy', alt: '' })`
  width: 90px;
  height: auto;
  object-fit: cover;
`;

const ThumbPlaceholder = styled.div`
  width: 90px;
  height: 160px;
  background-color: ${({ theme }) => theme.colors.grayscale};
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 200px;
`;

const StyledActionsWrap = styled(ActionsWrap)`
  z-index: 0;
`;

interface Props {
  project: VideoFactoryProject;
}

const HistoryTableRow: FC<Props> = ({ project }) => {
  const storySettings = project.settings.find(
    (s) => s.type === VideoFactorySettingsType.STORY_PROMPT,
  )!;

  const scriptSettings = project.settings.find(
    (s) => s.type === VideoFactorySettingsType.SCRIPT_PROMPT,
  )!;

  const [showAnimateImagesModal, setShowAnimateImagesModal] = useState(false);

  const onLogDownload = () => {
    download(JSON.stringify(project.log ?? {}), 'log.json');
  };

  const canCreateAnimatedVideo = useMemo(() => {
    if (
      ![
        VideoFactoryProjectStatus.COMPLETED,
        VideoFactoryProjectStatus.FAILED,
      ].includes(project.status)
    ) {
      return false;
    }

    if (
      DateTime.fromISO(project.created_at) <
      DateTime.fromISO('2025-01-31T08:18:57.113871Z')
    ) {
      return false;
    }

    return true;
  }, [project]);

  return (
    <tr key={project.id}>
      <Td>
        {project.thumbnail ? (
          <a
            href={project.video ?? undefined}
            target="_blank"
            rel="noreferrer noopener"
          >
            <Thumbnail src={project.thumbnail} />
          </a>
        ) : (
          <ThumbPlaceholder />
        )}
      </Td>
      <Td>{project.topic}</Td>
      <Td>
        <StatusBadge status={project.status} />
      </Td>
      <Td>
        {DateTime.fromISO(project.created_at).toFormat('dd/LL/yyyy')}
        <br />
        <Text as="span" $color="gray" $type="extraSmall">
          {DateTime.fromISO(project.created_at).toFormat('HH:mm')}
        </Text>
      </Td>
      <Td>
        {project.finished_at ? (
          <>
            {DateTime.fromISO(project.finished_at).toFormat('dd/LL/yyyy')}
            <br />
            <Text as="span" $color="gray" $type="extraSmall">
              {DateTime.fromISO(project.finished_at).toFormat('HH:mm')}
            </Text>
          </>
        ) : (
          '-'
        )}
      </Td>
      <Td>
        V{storySettings.version}
        {!!storySettings.category && (
          <>
            <br />
            <Text as="span" $color="gray" $type="extraSmall">
              {storySettings.category.name}
            </Text>
          </>
        )}
        <br />
        <Text as="span" $color="gray" $type="extraSmall">
          {DateTime.fromISO(storySettings.created_at).toFormat(
            'dd/LL/yyyy HH:mm',
          )}
        </Text>
      </Td>
      <Td>
        V{scriptSettings.version}
        <br />
        <Text as="span" $color="gray" $type="extraSmall">
          {DateTime.fromISO(scriptSettings.created_at).toFormat(
            'dd/LL/yyyy HH:mm',
          )}
        </Text>
      </Td>
      <Td>{project.error || '-'}</Td>
      <Td $actionsCol>
        <StyledActionsWrap>
          <StyledActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onLogDownload}>
              Download Log
            </ActionsDropdownButton>
            {canCreateAnimatedVideo && (
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowAnimateImagesModal(true)}
              >
                Create Animated Video
              </ActionsDropdownButton>
            )}
            {!!project.thumbnail && (
              <ActionsDropdownButton
                as="button"
                onClick={() =>
                  window.open(project.thumbnail!, '_blank')?.focus()
                }
              >
                Download Thumbnail
              </ActionsDropdownButton>
            )}
            {!!project.video && (
              <ActionsDropdownButton
                as="button"
                onClick={() => window.open(project.video!, '_blank')?.focus()}
              >
                Download Slideshow
              </ActionsDropdownButton>
            )}
            {!!project.animatedVideo && (
              <ActionsDropdownButton
                as="button"
                onClick={() =>
                  window.open(project.animatedVideo!, '_blank')?.focus()
                }
              >
                Download Animated Video
              </ActionsDropdownButton>
            )}
          </StyledActionsDropdown>
        </StyledActionsWrap>
      </Td>
      <AnimatePresence>
        {showAnimateImagesModal && (
          <AnimateImagesDrawer
            project={project}
            onClose={() => setShowAnimateImagesModal(false)}
          />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default HistoryTableRow;
